import type { WorkingCalendarDayOffs } from '../../../../api/workingCalendarApis';
import type { TargetsPeriodColumn } from './targetsPeriodUtils';

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function formatYmd(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/**
 * Portal Daily Targets parity: weekday week-offs + dated holidays.
 * Safe when dayOffs is null/partial.
 */
export function isNonWorkingDay(
  date: Date,
  dayOffs: WorkingCalendarDayOffs | null | undefined,
): boolean {
  if (!dayOffs) return false;
  const dayOfWeek = date.toLocaleString('en-US', { weekday: 'long' }).toUpperCase();
  const weekOffs = Array.isArray(dayOffs.weekOffDays) ? dayOffs.weekOffDays : [];
  if (weekOffs.includes(dayOfWeek)) return true;

  const year = String(date.getFullYear());
  const holidays = dayOffs.holidays?.[year];
  if (!Array.isArray(holidays) || holidays.length === 0) return false;
  return holidays.includes(formatYmd(date));
}

/**
 * Mark Day-scale period columns as non-working from project dayOffs.
 * Week / Month columns are left unchanged (portal does not block those).
 */
export function applyWorkingCalendarToColumns(
  columns: TargetsPeriodColumn[],
  dayOffs: WorkingCalendarDayOffs | null | undefined,
): TargetsPeriodColumn[] {
  if (!dayOffs || columns.length === 0) {
    return columns.map((col) =>
      col.frequency === 'DAY' ? { ...col, isNonWorking: false } : col,
    );
  }
  return columns.map((col) => {
    if (col.frequency !== 'DAY') return col;
    return {
      ...col,
      isNonWorking: isNonWorkingDay(col.start, dayOffs),
    };
  });
}
